import { ComponentProps, ReactElement, useMemo } from 'react';

import {
  faCircleQuestion,
  faGear,
  faHouse,
  faTools,
} from '@awesome.me/kit-379289146f/icons/classic/solid';
import {
  faCoreLogo,
  faEngageLogo,
  faProgressLogo,
  faProtectLogo,
  faTaxplanLogo,
} from '@awesome.me/kit-379289146f/icons/kit/custom';
import { AccessSidebar } from '@changegps/horizon-component-library';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useUserHasProductsAccess } from '@/features/users';
import { getSidebarHyperlink } from '@/utils';

export const AppSidebar = (): ReactElement => {
  const userHasAccess = useUserHasProductsAccess();

  const routes: ComponentProps<typeof AccessSidebar>['routes'] = useMemo(
    () => [
      // * Home
      {
        icon: <FontAwesomeIcon size="xs" icon={faHouse as IconProp} />,
        link: '/home',
        title: 'Home',
        id: 'home',
      },
      // * Engage
      {
        icon: <FontAwesomeIcon size="xs" icon={faEngageLogo as IconProp} />,
        hyperlink: getSidebarHyperlink({ product: 'engage' }),
        title: 'Engage',
        disabled: !userHasAccess['engage'],
        id: 'engage',
      },
      // * Tax Plan
      {
        icon: <FontAwesomeIcon size="xs" icon={faTaxplanLogo as IconProp} />,
        title: 'Tax Plan',
        hyperlink: getSidebarHyperlink({ product: 'taxplan' }),
        disabled: !userHasAccess['taxplan'],
        id: 'tax-plan',
      },
      // * Core
      {
        icon: <FontAwesomeIcon size="xs" icon={faCoreLogo as IconProp} />,
        title: 'Core',
        subRoutes: [
          // * Documents
          {
            title: 'Documents',
            routes: [
              {
                hyperlink: getSidebarHyperlink({
                  product: 'core',
                  subProduct: 'collate',
                }),
                title: 'Collate',
              },
              {
                hyperlink: getSidebarHyperlink({
                  product: 'core',
                  subProduct: 'div-statements',
                }),
                title: 'Dividend statements',
              },
              {
                hyperlink: getSidebarHyperlink({
                  product: 'core',
                  subProduct: 'letters',
                }),
                title: 'Letters (merge)',
              },
              {
                hyperlink: getSidebarHyperlink({
                  product: 'core',
                  subProduct: 'resources',
                }),
                title: 'Resources',
              },
              {
                hyperlink: getSidebarHyperlink({
                  product: 'core',
                  subProduct: 'trust-distribution',
                }),
                title: 'Trust distributions',
              },
            ],
          },
          // * Client Management
          {
            title: 'Client Management',
            routes: [
              {
                hyperlink: getSidebarHyperlink({
                  product: 'core',
                  subProduct: 'meetings',
                }),
                title: 'Actions',
              },
              {
                hyperlink: getSidebarHyperlink({
                  product: 'core',
                  subProduct: 'service-plan',
                }),
                title: 'Service plan & potential',
              },
            ],
          },
          // * Reports
          {
            title: 'Reports',
            routes: [
              {
                hyperlink: getSidebarHyperlink({
                  product: 'core',
                  subProduct: 'blueprint',
                }),
                title: 'Blueprint',
              },
              {
                hyperlink: getSidebarHyperlink({
                  product: 'core',
                  subProduct: 'cgt-advice',
                }),
                title: 'CGT advice',
              },
              {
                hyperlink: getSidebarHyperlink({
                  product: 'core',
                  subProduct: 'fbt',
                }),
                title: 'FBT report + decs',
              },
              {
                hyperlink: getSidebarHyperlink({
                  product: 'core',
                  subProduct: 'structure',
                }),
                title: 'Structure advice',
              },
            ],
          },
        ],
        disabled: !userHasAccess['core'],
        hideSelectedState: true,
      },
      // * Progress
      {
        icon: <FontAwesomeIcon size="xs" icon={faProgressLogo as IconProp} />,
        hyperlink: getSidebarHyperlink({ product: 'progress' }),
        title: 'Progress',
        disabled: !userHasAccess['progress'],
        id: 'progress',
      },
      // * Protect
      {
        icon: <FontAwesomeIcon size="sm" icon={faProtectLogo as IconProp} />,
        hyperlink: getSidebarHyperlink({
          product: 'protect',
        }),
        title: 'Protect',
        disabled: !userHasAccess['protect'],
        id: 'protect',
      },
      // * Settings
      {
        icon: <FontAwesomeIcon size="sm" icon={faGear as IconProp} />,
        link: '/settings',
        title: 'Settings',
        id: 'settings',
      }, // * Support
      {
        icon: <FontAwesomeIcon size="sm" icon={faCircleQuestion as IconProp} />,
        link: '/support',
        title: 'Support',
        id: 'support',
      },
      // * Admin Tools
      {
        icon: <FontAwesomeIcon size="sm" icon={faTools as IconProp} />,
        hyperlink: getSidebarHyperlink({ product: 'admin' }),
        title: 'Admin Tools',
        id: 'admin',
        hidden: !userHasAccess.admin,
      },
    ],
    [userHasAccess],
  );

  return <AccessSidebar routes={routes} />;
};
